import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';

import { eventFullPropTypeShape } from '../../proptypes';
import HeartButton from '../../containers/HeartButton';
import Tag from '../Tag';

import { srOnly } from '../../base-styles/mixins.module.css';
import { hasRegistrationPerks } from '../../utils/race';
import WithLazyBackgroundImage from '../../shared/WithLazyBackgroundImage';

import {
  card,
  details,
  gradient,
  header,
  heart,
  image as imageStyle,
  imageContainer,
  info,
  link,
  location as locationStyle,
  photoOnlyLink,
  priceRange,
  registrationPerks,
  reviewEvent,
  saveEventContainer,
  strapline,
  tag,
  tagContainer,
  title as titleStyle,
  titleAndRegistrationPerks,
} from './styles.module.css';

/**
 * Event card presentational component.
 */
// TODO:
// 1) Please fix this parameter passing.
// 2) Split up some of this into its own components (for example,
// the items wrapped in logic depending on isReviewPage).
const EventCard = ({
  className,
  children,
  description,
  id,
  images,
  lazy,
  linkProps,
  location,
  notifications,
  organiser,
  permalink,
  races,
  reviewPermalink,
  isReviewPage = false,
  saves,
  source,
  startDate,
  startDateFormatted,
  startMonthFormatted,
  title,
}) => {
  const bookingEnabled =
    races && races.bookingEnabled ? races.bookingEnabled : false;
  const LazyWrapper = lazy ? WithLazyBackgroundImage : React.Fragment;
  return (
    <article className={[card, className].join(' ')}>
      <header className={header}>
        <div className={tagContainer}>
          {(!isReviewPage &&
            races &&
            races.bookingPartner &&
            races.bookingPartner.id !== 3 && (
              <Tag text={races.bookingPartner.value} />
            )) ||
            null}

          {!isReviewPage &&
            notifications &&
            notifications.map(({ type, value }) => (
              <Tag key={type} className={tag} text={value} red={type < 3} />
            ))}
        </div>
        <div className={imageContainer}>
          <LazyWrapper>
            <div
              style={{ backgroundImage: `url(${images.hero.medium})` }}
              className={imageStyle}
              alt=""
            />
          </LazyWrapper>
        </div>
        <div className={gradient} />
        <div className={info}>
          <div
            className={srOnly}
            /* eslint-disable-next-line react/no-danger */
            dangerouslySetInnerHTML={{ __html: description }}
          />
          <div className={titleAndRegistrationPerks}>
            <h2 className={titleStyle}>{title}</h2>
            {hasRegistrationPerks(races) && (
              <span className={registrationPerks} />
            )}
          </div>
          {isReviewPage && (
            <h2 className={titleStyle}>{startMonthFormatted}</h2>
          )}
          {!isReviewPage && (
            <div className={locationStyle}>
              <span className={details}>
                {startDate && (
                  <time dateTime={startDate}>{startDateFormatted}</time>
                )}
                {location && location.address && (
                  <span>
                    <span> • {location.address.join(', ')}</span>
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      </header>
      <div className={strapline}>
        <span>
          {!isReviewPage &&
            races &&
            races.races &&
            (races.races.length > 1
              ? `${races.races.length} races`
              : `${races.races.length} race`)}
          {(!races || !races.races) && 'More Information'}
        </span>
        {!isReviewPage && races && races.pricesFromFormatted && (
          <div className={priceRange}>
            {/* eslint-disable no-nested-ternary */
            races.standardTickets ? (
              <div>
                {races.pricesFromFormatted}
                {races.pricesToFormatted && (
                  <span>–{races.pricesToFormatted}</span>
                )}
              </div>
            ) : races.charityTickets ? (
              'Charity Tickets'
            ) : races.ballotTickets ? (
              'Ballot Tickets'
            ) : null
            /* eslint-enable */
            }
          </div>
        )}
        {isReviewPage && reviewPermalink && (
          <Link to={reviewPermalink} className={reviewEvent}>
            Review this event
          </Link>
        )}
      </div>
      <Link
        to={{
          pathname: permalink,
          hash: source ? `#source=${source}` : undefined,
        }}
        className={isReviewPage ? photoOnlyLink : link}
        {...linkProps}
      >
        <span className={srOnly}>{title}</span>
      </Link>
      <div className={saveEventContainer}>
        <HeartButton
          id={id}
          count={saves ? saves.total : 0}
          bookingPartner={races && races.bookingPartner}
          className={heart}
          trackingObject={{
            id,
            title,
            organiser,
            startDate,
            tags: notifications,
            bookingEnabled,
          }}
        >
          <div className="heart" aria-hidden="true" />
        </HeartButton>
      </div>
      {children}
    </article>
  );
};

EventCard.propTypes = {
  /**
   * String class names to append to component styles.
   */
  className: PropTypes.string,
  /**
   * Children contents of button.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   * Numeric ID of the event.
   */
  id: eventFullPropTypeShape.id, // eslint-disable-line react/require-default-props
  /**
   * Boolean whether or not this is an event review card.
   */
  isReviewPage: PropTypes.bool,
  /**
   * Object of images associated with the event.
   */
  images: eventFullPropTypeShape.images,
  /**
   * Lazy: Should the background image be loaded lazily
   */
  lazy: PropTypes.bool,
  /**
   * Additional properties to add to the permalink.
   */
  linkProps: PropTypes.shape(),
  /**
   * Object of location data.
   */
  location: eventFullPropTypeShape.location,
  /**
   * Array of notification objects.
   */
  notifications: eventFullPropTypeShape.notifications,
  /**
   * Event organiser object.
   */
  organiser: eventFullPropTypeShape.organiser,
  /**
   * String permalink to the event page.
   */
  permalink: eventFullPropTypeShape.permalink, // eslint-disable-line react/require-default-props
  /**
   * Object of race data.
   */
  races: eventFullPropTypeShape.races,
  /**
   * Link to the event review page.
   */
  reviewPermalink: PropTypes.string,
  /**
   * Object of event saves data.
   */
  saves: eventFullPropTypeShape.saves,
  /**
   * Optional source location of card.
   */
  source: PropTypes.string,
  /**
   * ISO string of event start date.
   */
  startDate: eventFullPropTypeShape.startDate, // eslint-disable-line react/require-default-props
  /**
   * Formatted string of race start date.
   */
  startDateFormatted: eventFullPropTypeShape.startDateFormatted, // eslint-disable-line max-len, react/require-default-props
  /**
   * String title of the event.
   */
  title: eventFullPropTypeShape.title, // eslint-disable-line react/require-default-props
  /**
   * Stringdescriptiontitle of the event.
   */
  description: eventFullPropTypeShape.description,
};

EventCard.defaultProps = {
  children: [],
  className: '',
  description: undefined,
  images: undefined,
  lazy: false,
  linkProps: {},
  location: undefined,
  notifications: undefined,
  organiser: null,
  races: undefined,
  reviewPermalink: '',
  isReviewPage: undefined,
  saves: undefined,
  source: undefined,
};

export default EventCard;
